import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Accordion,
  AccordionPanel,
  Text,
  TextInput,
  FormField,
  TextArea,
  Select,
} from "grommet";
import {
  Trash,
  Edit,
  Add,
  Undo,
  FormDown,
  FormUp,
  Search,
} from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { TrainingDataService } from "../../../services";
import { presentAlertPrompt } from "@shared/Alert";
import TrainingDataStatus from "@shared/TrainingDataStatus";
import { useTranslation } from "react-i18next";

export default function TrainingDataList({
  internal,
  refreshSignal,
  onRefresh: parentRefresh,
}) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [trainingData, setTrainingData] = useState();
  const [activeIndex, setActiveIndex] = useState();
  const [sortBy, setSortBy] = useState("updated"); // 'updated', 'question', 'status'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIn, setSearchIn] = useState("all");
  const questionInputRef = React.useRef(null);

  React.useEffect(() => {
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internal, refreshSignal]);

  const onRefresh = () => {
    TrainingDataService.getTrainingData(internal).then(
      (response) => {
        // Exclude auto-generated Q/A pairs from knowledge base documents — those
        // are managed transparently via the Knowledge Base tab.
        const filtered = (response.data || []).filter(
          (item) => !item.sourceType || item.sourceType === "qa",
        );
        setList(filtered);
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  // Entries not yet embedded, or marked for removal — invisible to guests/staff until the model is published.
  const pendingCount = useMemo(
    () => list.filter((item) => !item.inModel || item.willBeRemoved).length,
    [list],
  );

  const sortedList = useMemo(() => {
    // 1. Filter by search query
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? list.filter((item) => {
          if (searchIn === "question")
            return (item.question || "").toLowerCase().includes(q);
          if (searchIn === "answer")
            return (item.answer || "").toLowerCase().includes(q);
          return (
            (item.question || "").toLowerCase().includes(q) ||
            (item.answer || "").toLowerCase().includes(q)
          );
        })
      : list;

    // 2. Sort
    const sorted = [...filtered].sort((a, b) => {
      let compareA, compareB;

      if (sortBy === "question") {
        compareA = (a.question || "").toLowerCase();
        compareB = (b.question || "").toLowerCase();
      } else if (sortBy === "status") {
        // Sort by status priority: willBeRemoved, inModelAt, new
        const getStatusPriority = (item) => {
          if (item.willBeRemoved) return 3;
          if (item.inModelAt) return 2;
          return 1;
        };
        compareA = getStatusPriority(a);
        compareB = getStatusPriority(b);
      } else {
        // Default: sort by 'updated' or createdAt
        compareA = new Date(a.updated || a.createdAt || 0);
        compareB = new Date(b.updated || b.createdAt || 0);
      }

      if (compareA < compareB) return sortOrder === "asc" ? -1 : 1;
      if (compareA > compareB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [list, sortBy, sortOrder, searchQuery, searchIn]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder(field === "updated" ? "desc" : "asc");
    }
  };

  const handleSave = () => {
    if (!trainingData._id) {
      TrainingDataService.add(trainingData).then(
        async (response) => {
          handleActivateAccordionPanel();
          presentToastSuccess(response.data?.message || "Success");
          onRefresh();
          if (parentRefresh) parentRefresh();
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    } else {
      TrainingDataService.update(trainingData).then(
        async (response) => {
          handleActivateAccordionPanel();
          presentToastSuccess(response.data?.message || "Success");
          onRefresh();
          if (parentRefresh) parentRefresh();
        },
        (error) => {
          presentToastErrorContent(error);
        },
      );
    }
  };

  const handleAdd = () => {
    const nextTrainingData = {
      question: "New question",
      answer: "",
      internal,
      updated: new Date().toISOString(), // Add current timestamp to ensure it appears first when sorted
    };
    setTrainingData(nextTrainingData);
    // Add new item to the list and it will automatically appear at top due to recent updated timestamp
    const newList = [nextTrainingData, ...list];
    setList(newList);
    // Open the first accordion item (index 0) after a brief delay to ensure state is updated
    setTimeout(() => {
      setActiveIndex(0);
    }, 50);
  };

  const handleRemoveTrainingData = (trainingData, toRemove) => {
    setTimeout(() => handleActivateAccordionPanel(undefined), 1);
    const definitive = !trainingData?._id || !trainingData?.inModelAt;
    if (toRemove) {
      presentAlertPrompt({
        title: definitive
          ? t("trainingData.deleteDefinitiveTitle")
          : t("trainingData.deleteMarkedTitle"),
        message: definitive
          ? t("trainingData.deleteDefinitiveMessage")
          : t("trainingData.deleteMarkedMessage"),
        onOK: () => {
          if (!trainingData?._id) {
            setList(list.filter((item) => item !== trainingData));
          } else {
            removeTrainingData(trainingData._id, true);
          }
        },
        buttonOKText: t("trainingData.deleteConfirm"),
        buttonOKColor: "status-critical",
      });
    } else if (trainingData?._id) {
      removeTrainingData(trainingData._id, false);
    }
  };

  const removeTrainingData = (trainingDataId, toRemove) => {
    TrainingDataService.deleteTrainingData(trainingDataId, toRemove).then(
      (response) => {
        onRefresh();
        if (parentRefresh) parentRefresh();
        presentToastSuccess(t("trainingData.deletedSuccess"));
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  function handleInputChange({ target: { name, value } }) {
    var values = { ...trainingData };
    values[name] = value;
    setTrainingData(values);
  }

  const handleQuestionFocus = (event) => {
    if (trainingData?.question === "New question") {
      event.target.select();
    }
  };

  const handleActivateAccordionPanel = (indexes) => {
    if (indexes === undefined) {
      setActiveIndex([]);
      setTrainingData(undefined);
      return;
    }
    const nextIndex = indexes[0];
    setActiveIndex(nextIndex);
    setTrainingData(sortedList[nextIndex]);
  };

  const SortableHeader = ({ label, field }) => (
    <Button
      plain
      onClick={() => handleSort(field)}
      style={{ fontWeight: "bold" }}
    >
      <Box direction="row" align="center" gap="xsmall">
        <Text weight="bold">{label}</Text>
        {sortBy === field &&
          (sortOrder === "asc" ? (
            <FormUp size="small" />
          ) : (
            <FormDown size="small" />
          ))}
      </Box>
    </Button>
  );

  return (
    <Box gap="medium" justify="start" fill>
      <Box direction="row" justify="start" align="center" gap="small">
        <Button
          primary
          icon={<Add size="small" />}
          label={t("trainingData.addButton")}
          onClick={handleAdd}
          size="small"
        />
      </Box>

      {pendingCount > 0 && (
        <Box
          direction="row"
          align="center"
          gap="small"
          background="light-2"
          round="xsmall"
          pad={{ horizontal: "small", vertical: "xsmall" }}
          border={{ color: "status-warning", side: "left", size: "medium" }}
        >
          <Text size="small" weight="bold" color="status-warning">
            {t("trainingData.pendingCount", { count: pendingCount })}
          </Text>
          <Text size="small" color="dark-4">
            {t("trainingData.pendingHint")}
          </Text>
        </Box>
      )}

      {/* Search bar */}
      <Box direction="row" gap="small" align="center" fill="horizontal">
        <Box fill>
          <TextInput
            icon={<Search size="small" />}
            placeholder={t("trainingData.searchPlaceholder")}
            value={searchQuery}
            onChange={({ target: { value } }) => setSearchQuery(value)}
          />
        </Box>
        <Box flex={false} style={{ minWidth: 160 }}>
          <Select
            options={[
              { label: t("trainingData.searchInAll"), value: "all" },
              { label: t("trainingData.searchInQuestion"), value: "question" },
              { label: t("trainingData.searchInAnswer"), value: "answer" },
            ]}
            labelKey="label"
            valueKey={{ key: "value", reduce: true }}
            value={searchIn}
            onChange={({ value }) => setSearchIn(value)}
          />
        </Box>
        {searchQuery.trim() && (
          <Text size="small" color="dark-4" style={{ whiteSpace: "nowrap" }}>
            {t("trainingData.resultCount", { count: sortedList.length })}
          </Text>
        )}
      </Box>

      {/* Column Headers */}
      <Box
        direction="row"
        align="center"
        pad={{ horizontal: "small", vertical: "xsmall" }}
        gap="small"
        fill="horizontal"
        background="light-2"
        round="xsmall"
      >
        <Box width={{ min: "120px" }}>
          <SortableHeader
            label={t("trainingData.statusColumn")}
            field="status"
          />
        </Box>
        <Box fill>
          <SortableHeader
            label={t("trainingData.questionColumn")}
            field="question"
          />
        </Box>
        <Box width={{ min: "70px" }}>
          <Text weight="bold">{t("trainingData.actionsColumn")}</Text>
        </Box>
      </Box>

      <Box align="center" fill="horizontal">
        <Accordion
          width="xlarge"
          activeIndex={activeIndex}
          onActive={handleActivateAccordionPanel}
        >
          {sortedList.map((item, index) => (
            <AccordionPanel
              key={item._id || index}
              label={
                <Box
                  direction="row"
                  align="center"
                  pad="small"
                  gap="small"
                  alignContent="start"
                  fill
                >
                  <Box width={{ min: "120px" }}>
                    <TrainingDataStatus trainingData={item} />
                  </Box>
                  <Box fill>
                    <Text margin={{ left: "small" }} truncate>
                      {item?.question}
                    </Text>
                  </Box>
                  <Box direction="row" gap="small" width={{ min: "70px" }}>
                    <Button
                      icon={<Edit size="small" />}
                      size="small"
                      primary
                      color="status-warning"
                      onClick={() => handleActivateAccordionPanel([index])}
                    />
                    <Button
                      icon={
                        item?.willBeRemoved ? (
                          <Undo size="small" />
                        ) : (
                          <Trash size="small" />
                        )
                      }
                      size="small"
                      primary
                      color={
                        item?.willBeRemoved ? "accent-1" : "status-critical"
                      }
                      onClick={() =>
                        handleRemoveTrainingData(item, !item?.willBeRemoved)
                      }
                    />
                  </Box>
                </Box>
              }
            >
              <Box pad="small" background="light-3">
                <FormField label={t("trainingData.questionLabel")} width="100%">
                  <TextInput
                    ref={questionInputRef}
                    value={trainingData?.question}
                    name="question"
                    onChange={handleInputChange}
                    onFocus={handleQuestionFocus}
                  />
                </FormField>
                <FormField label={t("trainingData.answerLabel")} width="100%">
                  <TextArea
                    fill
                    value={trainingData?.answer}
                    name="answer"
                    onChange={handleInputChange}
                    style={{ width: "100%", height: 200 }}
                  />
                </FormField>
                <Box
                  as="footer"
                  gap="small"
                  direction="row"
                  align="center"
                  justify="end"
                  pad={{ top: "medium", bottom: "small" }}
                  fill="horizontal"
                >
                  <Button
                    label={t("trainingData.cancel")}
                    onClick={() => handleActivateAccordionPanel()}
                    color="dark-3"
                  />
                  <Button
                    label={t("trainingData.save")}
                    onClick={handleSave}
                    primary
                    color="status-ok"
                  />
                </Box>
              </Box>
            </AccordionPanel>
          ))}
        </Accordion>
      </Box>
    </Box>
  );
}
